import clsx from 'clsx';
import {
  useId,
  useMemo,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  Children,
  cloneElement,
} from 'react';
import type {
  GroupBase,
  ControlProps as ReactSelectControlProps,
} from 'react-select';

import { Button } from '@dynatrace/strato-components/buttons';
import { _mergeProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import { HiddenSelectV2 } from './HiddenSelect.js';
import { _isValueContainerComponent } from './ValueContainer.js';
import { useFormControlState } from '../../../core/hooks/useFormControlState.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { OverlayStackingProvider } from '../../../core/providers/OverlayStackingProvider.js';
import { useFocusRing } from '../../../core/styles/useFocusRing.js';
import { InternalBaseItem as BaseItem } from '../../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import { useSelectMenuContext } from '../hooks/use-select-menu-context.js';
import { SelectActions } from '../state/select-menu-state-reducer.js';
import {
  selectTriggerCSS,
  selectTriggerIconWrapperCSS,
} from '../styles/Select.sty.js';

//#region Typings
type ControlProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectControlProps<OptionValue, isMulti, Group>;
//#endregion Typings

/** Iterates over child nodes to extract the value container. */
function getSelectValueContainer<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(children: ReactNode) {
  const childrenArray = Children.toArray(children);

  for (const child of childrenArray) {
    if (_isValueContainerComponent<OptionValue, isMulti, Group>(child)) {
      return child;
    }
  }

  // Assertion to control the return type, we should always get a valueContainer.
  throw new Error('Assertion to make sure the container exists.');
}

export function Control<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(props: Readonly<ControlProps<OptionValue, isMulti, Group>>) {
  const { children, isDisabled, innerRef, innerProps, selectProps } = props;
  const { 'aria-labelledby': ariaLabelledBy, ...ariaLabelingProps } =
    selectProps.ariaLabelingProps ?? {};
  const {
    className: consumerClassName,
    style: consumerStyle,
    // The select testing helpers use the trigger test id for various actions, keeping the default for now.
    'data-testid': dataTestId = 'selectv2-trigger',
    readOnly,
    ariaDisabled,
  } = selectProps.componentProps.trigger ?? {};
  const rootRef = useMergeRefs<HTMLElement | null>([
    innerRef,
    selectProps.setTriggerRef,
  ]);

  const valueContainer = getSelectValueContainer(children);
  const triggerId = `strato-select-trigger${useId()}`;
  const customTriggerId = selectProps.customTrigger?.props?.id;

  const { state } = useFormFieldMessagesContext();
  /** Please remove with removal of control state. */
  const [hasFormControlError, hint] = useFormControlState(
    selectProps.controlState,
  );

  const showError = selectProps.controlState
    ? hasFormControlError
    : state.showError;

  const hiddenReactSelectInput = Array.isArray(valueContainer.props.children)
    ? valueContainer.props.children[1]
    : null;
  const id = selectProps['selectInputId'];

  const hiddenInput = useMemo(
    () =>
      cloneElement(hiddenReactSelectInput, {
        ...hiddenReactSelectInput.props,
        key: 'hidden-react-select-input',
        id,
        'aria-invalid': showError,
        'aria-labelledby': ariaLabelledBy ?? customTriggerId ?? triggerId,
        ...ariaLabelingProps,
      }),
    [
      hiddenReactSelectInput,
      id,
      showError,
      ariaLabelledBy,
      customTriggerId,
      triggerId,
      ariaLabelingProps,
    ],
  );

  const { focusProps, focusClassName } = useFocusRing({
    color: showError ? 'critical' : 'neutral',
    focusWithin: true,
    disabled: isDisabled,
  });

  const { dispatch } = useSelectMenuContext();

  const interactionProps = useMemo(() => {
    return {
      onTouchStart: () => {
        dispatch({
          type: SelectActions.SELECTFOCUS,
        });
      },
      onClick: () => {
        dispatch({
          type: SelectActions.SELECTFOCUS,
        });
      },

      onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
        if (
          (event.key === 'Enter' || event.key === ' ') &&
          !selectProps.isMulti
        ) {
          dispatch({
            type: SelectActions.CLOSE,
          });
        }
      },
    };
  }, [dispatch, selectProps.isMulti]);

  const customTrigger = useMemo(() => {
    if (selectProps.customTrigger) {
      /** React Select dummyInput is applied in the ValueContainers children in order to
       * handle / react to the inputs correctly. This one needs to be passed through in order for the
       * trigger to work correctly.
       */

      return cloneElement(
        selectProps.customTrigger,
        {
          key: 'custom-trigger',
          ref: rootRef,
          id: triggerId,
          ..._mergeProps(
            selectProps.customTrigger.props,
            innerProps,
            interactionProps,
          ),
        },
        [selectProps.customTrigger.props?.children, hiddenInput],
      );
    }
  }, [
    hiddenInput,
    innerProps,
    interactionProps,
    rootRef,
    selectProps.customTrigger,
    triggerId,
  ]);

  if (customTrigger) {
    return <OverlayStackingProvider>{customTrigger}</OverlayStackingProvider>;
  }

  return (
    <Flex padding={0} flexDirection="column" gap={4}>
      <OverlayStackingProvider>
        <Button
          ref={rootRef}
          id={triggerId}
          textAlign="start"
          data-testid={dataTestId}
          disabled={isDisabled}
          aria-disabled={ariaDisabled}
          width={selectProps.triggerWidth}
          {...innerProps}
          variant="emphasized"
          {...interactionProps}
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          readOnly={readOnly}
          color={showError ? 'critical' : 'neutral'}
          className={clsx(
            'strato-components-selectv2-control-trigger',
            selectTriggerCSS({
              fullWidth:
                selectProps.triggerWidth === 'full' ||
                selectProps.triggerWidth === '100%',
            }),
            focusClassName,
            consumerClassName,
          )}
          style={consumerStyle}
          as="div"
          {...focusProps}
        >
          {selectProps.prefix && (
            <Button.Prefix
              data-testid={selectProps.prefix.props['data-testid']}
              className={selectProps.prefix.props.className}
              style={selectProps.prefix.props.style}
            >
              {selectProps.prefix.props.children}
            </Button.Prefix>
          )}
          <Button.Label>
            {hiddenInput}
            {valueContainer}
          </Button.Label>
          <Button.Suffix>
            <div
              className={clsx(
                selectTriggerIconWrapperCSS({
                  isOverlayOpen: selectProps.menuIsOpen,
                }),
              )}
            >
              <ChevronDownSmallIcon />
            </div>
          </Button.Suffix>
        </Button>
      </OverlayStackingProvider>
      <HiddenSelectV2<OptionValue>
        ref={selectProps.hiddenSelectRef}
        required={selectProps.isRequired}
        selectedValues={selectProps.value}
        name={selectProps.hiddenSelectName}
        disabled={isDisabled || readOnly}
        controlState={selectProps.controlState}
      />
      {hint && (
        <BaseItem
          variant={showError ? 'error' : 'hint'}
          disabled={isDisabled || ariaDisabled}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
}
